<template>
  <div>
    <div class="FormBuilder-container Content">
      <event-stage-header />
      <screen-selection-menu
        v-show="ui.showScreenNavigationMenu && screens.length > 1"
        @screen-selection-menu:close="handleCloseScreenNavigationMenu"
      />

      <theme-button @toggle-theme-builder="toggleThemeBuilder" />

      <div class="SectionContainer u-flex">
        <section :class="[{'is-loading': ui.isLoading }, 'SectionContainer-left']">
          <div
            v-if="!ui.isLoading"
            class="DynamicForm-wrapper"
          >
            <div
              v-if="showQuestionHasLogicIcons"
              class="QuestionOptions-container"
            >
              <tooltip
                class="Custom-tooltip"
                position="bottom"
              >
                <span
                  slot="trigger"
                  class="u-flex"
                >
                  <span
                    v-if="currentFormScreenQuestion.has_flows"
                    class="Hint Icon-s Icon-project-diagram"
                  />
                  <span
                    v-if="currentFormScreenQuestion.has_scores"
                    class="Hint Icon-s Icon-sort-numeric-up"
                  />
                  <span
                    v-if="currentFormScreenQuestion.has_files"
                    class="Hint Icon-s Icon-file"
                  />
                </span>
                <span slot="tip">
                  This question has <span v-if="currentFormScreenQuestion.has_flows"> Screen flows </span><span v-if="currentFormScreenQuestion.has_scores"> Scores </span><span v-if="currentFormScreenQuestion.has_files"> Files </span>
                </span>
              </tooltip>

              <span
                v-if="currentFormScreenQuestion.is_hidden"
                class="Hint HiddenQuestion Icon-s Icon-eye-slash"
              />
            </div>
            <screen-actions-menu
              :screen="screens[currentScreen - 1]"
              @screen-actions-menu:replace-question="handleReplaceQuestion"
            />

            <div
              v-for="screen in screens"
              v-show="currentScreen === screen.number"
              :key="screen.number"
              class="Screen-container"
            >
              <dynamic-form-renderer
                v-if="!ui.isLoading"
                :screen="screen"
              />
            </div>
            <div
              class="FormScreensDraggable-container"
            >
              <draggable
                v-model="formScreens"
                class="Dynamic-FormBuilder list-group"
                group="questions"
                draggable=".list-group-item"
                @change="handleDragQuestionToNewScreen"
              >
                <template slot="header">
                  <div
                    class="AddScreenHint"
                    @click="handleAddFormScreen(currentScreen)"
                  >
                    <span>Add a screen</span>
                  </div>
                </template>
              </draggable>
            </div>
            <div
              :class="['ScreenActions', { 'disabled' : selectedQuestion.vuexId !== null}]"
            >
              <button
                class="Button Button-transparent ScreenNavigationArrow"
                @click.stop="toggleShowScreenMenu"
              >
                <span class="Icon Icon-list" />
              </button>

              <button
                :disabled="currentScreen === 1"
                class="Button Button-transparent ScreenNavigationArrow"
                @click="decrementCurrentFormScreen"
              >
                <span class="Icon Icon-arrow-up" />
              </button>
              <button
                :disabled="currentScreen === screens.length"
                class="Button Button-transparent ScreenNavigationArrow"
                @click="incrementCurrentFormScreen"
              >
                <span class="Icon Icon-arrow-down" />
              </button>
            </div>
          </div>
        </section>
        <section
          class="QuestionBuilder SectionContainer-right"
          :style="`min-height:${ui.questionBuilderHeight}`"
        >
          <theme-builder
            v-click-outside="toggleThemeBuilder"
            :show-theme-builder="ui.showThemeBuilder"
          />

          <header-creation-steps
            selected="Form Builder"
            :event="event"
          />

          <question-list
            :question-to-replace="questionToReplace"
            @question-list:select-question="handleSelectQuestionToReplace"
            @question-list:cancel-replace-question="questionToReplace.from = null"
          />

          <replace-question-modal
            v-model="ui.showReplaceModal"
            :question-from="questionToReplace.from"
            :question-to="questionToReplace.to"
            @replace-question:submit="handleReplaceNewQuestionSubmit"
          />

          <div v-if="selectedQuestion.question_uuid">
            <question-builder
              :key="selectedQuestion.vuexId"
            />
          </div>
        </section>
      </div>
      <footer-creation-buttons
        v-if="isEventCreationRoute"
        class="FooterActionButtons"
      >
        <template #left-aligned-buttons>
          <router-link :to="{ name: 'basic-info', params: { id: $route.params.id }}">
            <button class="Button Button--green-outline">
              Back
            </button>
          </router-link>
        </template>
        <template #right-aligned-buttons>
          <button
            :class="[{'is-loading': ui.saveLoading }, 'Button Button--blue']"
            :disabled="formQuestions.length === 0 || selectedQuestion.id !== null || ui.disableSaveButtons"
            @click="handleSaveEvent(false)"
          >
            Save
          </button>
          <button
            :class="[{'is-loading': ui.submitLoading }, 'Button Button--green']"
            :disabled="formQuestions.length === 0 || selectedQuestion.id !== null || ui.disableSaveButtons"
            @click="handleSaveEvent"
          >
            Save & Continue
          </button>
        </template>
      </footer-creation-buttons>
    </div>
  </div>
</template>

<script>
import {
  mapState, mapMutations, mapActions, mapGetters,
} from 'vuex';
import Draggable from 'vuedraggable';
import Tooltip from '../../../common/tooltip.vue';
import HeaderCreationSteps from '../../common/header-creation-steps.vue';
import FooterCreationButtons from '../../common/footer-creation-buttons.vue';
import QuestionBuilder from '../../question-builder/question-builder.vue';
import DynamicFormRenderer from '../dynamic-form-renderer/dynamic-form-renderer';
import QuestionList from '../../question-builder/question-list.vue';
import ScreenSelectionMenu from './config-menus/screen-selection-menu';
import ScreenActionsMenu from './config-menus/screen-actions-menu';
import ReplaceQuestionModal from '../../modals/replace-question.vue';
import EventStageHeader from '../../common/event-stage-header';
import ThemeButton from '../../theme-builder/theme-button.vue';
import ThemeBuilder from '../../theme-builder/theme-builder.vue';

export default {
  components: {
    Tooltip,
    EventStageHeader,
    Draggable,
    HeaderCreationSteps,
    FooterCreationButtons,
    QuestionBuilder,
    DynamicFormRenderer,
    QuestionList,
    ScreenSelectionMenu,
    ScreenActionsMenu,
    ReplaceQuestionModal,
    ThemeButton,
    ThemeBuilder,
  },
  data() {
    return {
      formScreens: [],
      question: null,
      questionToReplace: {
        to: null,
        from: null,
      },
      ui: {
        isLoading: false,
        saveLoading: false,
        visibleScreenNumber: 1,
        showReplaceModal: false,
        submitLoading: false,
        brandImageSelected: false,
        questionBuilderHeight: '600px',
        showScreenNavigationMenu: false,
        showQuestionMenu: false,
        disableSaveButtons: false,
        showThemeBuilder: false,
      },
    };
  },
  computed: {
    ...mapState('events', {
      event: (state) => state.event,
    }),
    ...mapGetters('forms', ['getCurrentDynamicFormQuestion', 'dynamicFormHasLogic']),
    ...mapState('forms', {
      selectedQuestion: (state) => state.selectedQuestion,
      formQuestions: (state) => state.form.questions,
      screens: (state) => state.screens,
      currentScreen: (state) => state.currentScreen,
      logicWarningsShown: (state) => state.logicWarningsShown,
    }),

    isEventCreationRoute() {
      return this.$route.path.indexOf('create-event') > -1;
    },

    currentFormScreenQuestion() {
      return this.getCurrentDynamicFormQuestion;
    },

    showQuestionHasLogicIcons() {
      return this.currentFormScreenQuestion && (this.getCurrentDynamicFormQuestion.has_scores || this.getCurrentDynamicFormQuestion.has_flows);
    },
  },

  mounted() {
    window.scrollTo(0, 0);
    this.setCurrentScreenNumber(1);

    if (this.$route.params.id !== undefined) {
      this.ui.isLoading = true;

      this.getEvent(this.$route.params.id)
        .then(() => {
          this.populateScreens();
          if (this.event.is_locked) {
            this.$router.push({
              name: 'event-records',
              params: { eventId: this.event.id },
            });
          } else {
            this.ui.isLoading = false;
          }
        })
        .then(
          this.getThemes(this.$route.params.id),
        )
        .catch(({ data }) => {
          this.ui.isLoading = false;
          this.$bus.$emit('displayMsg', {
            text: data.message,
            type: 'warning',
            duration: 5000,
          });
        });
    }
  },
  methods: {
    ...mapActions('events', [
      'getEvent',
      'updateEvent',
    ]),
    ...mapMutations('forms', [
      'setFormQuestions',
      'setFormScreens',
      'resetSelectedQuestion',
      'setCurrentScreenNumber',
      'addFormScreen',
      'addQuestionToScreen',
      'incrementCurrentFormScreen',
      'decrementCurrentFormScreen',
      'updateLogicWarnings',
    ]),
    ...mapActions('themes', ['getThemes']),

    populateScreens() {
      if (this.formQuestions.length) this.setFormScreens();
    },

    handleAddFormScreen(currentScreen) {
      if (!this.logicWarningsShown.addQuestion && this.dynamicFormHasLogic) {
        this.$bus.$emit('displayMsg', {
          text: 'Adding or removing screens can change screen flows and scores already set up for this form. Please check them again in the Logic tab',
          type: 'warning',
          duration: 8000,
        });
        this.updateLogicWarnings('addQuestion');
      }
      this.addFormScreen(currentScreen);
    },

    handleDragQuestionToNewScreen(event) {
      this.addFormScreen(this.currentScreen);
      this.addQuestionToScreen({
        screenNumber: this.currentScreen,
        question: event.added.element,
      });
    },

    handleReplaceQuestion(question) {
      this.questionToReplace.from = question;
      // this.ui.showReplaceModal
    },

    handleSelectQuestionToReplace(question) {
      if (this.questionToReplace.from !== null) {
        this.questionToReplace.to = question;
        this.ui.showReplaceModal = true;
      }
    },

    handleScroll() {
      // Set container height to enable question list to remain sticky - scroll down the page pinned to top
      this.ui.questionBuilderHeight = `${document.querySelector('.SectionContainer-left').clientHeight}px`;
    },

    handleSelectScreen(number) {
      this.setCurrentScreenNumber(number);
      this.toggleShowScreenMenu();
    },

    handleCloseScreenNavigationMenu() {
      this.ui.showScreenNavigationMenu = false;
    },

    toggleShowScreenMenu() {
      this.ui.showScreenNavigationMenu = !this.ui.showScreenNavigationMenu;
    },

    handleSaveEvent(shouldNavigateToNextStage = true) {
      if (shouldNavigateToNextStage) {
        this.ui.submitLoading = true;
      } else {
        this.ui.saveLoading = true;
      }

      const questions = this.getSanitizedFormInputsForSubmission();

      this.updateEvent({
        ...this.event,
        questions,
      })
        .then((data) => {
          if (shouldNavigateToNextStage) {
            this.ui.submitLoading = false;
            this.$router.push({
              name: 'logic-dynamic-form',
              params: { id: this.event.id },
            });
          } else {
            this.ui.saveLoading = false;

            this.setFormQuestions(data.questions);
            this.setFormScreens();

            if (data.is_locked) {
              this.ui.disableSaveButtons = true;

              this.$bus.$emit('displayMsg', {
                text: 'The event is locked and because records are currently being reprocessed. Please check back soon.',
                type: 'error',
                duration: 25000,
              });

              setTimeout(() => {
                this.$router.push({
                  name: 'event-records',
                  params: { eventId: this.event.id },
                });
              }, 2000);
              return;
            }

            this.$bus.$emit('displayMsg', {
              text: 'Form saved',
              type: 'success',
            });
          }
        })
        .catch(({ data }) => {
          if (shouldNavigateToNextStage) {
            this.ui.submitLoading = false;
          } else {
            this.ui.saveLoading = false;
          }

          // Important: Form Questions need to be set with vuexIds since they were stripped before submit
          this.setFormQuestions(questions);
          this.setFormScreens();

          const message = data.meta !== undefined && data.meta.length ? data.meta[0].message : data.data.message;

          this.$bus.$emit('displayMsg', {
            text: message,
            type: 'error',
            duration: 25000,
          });

          if (data.type === 'Locked') {
            this.ui.disableSaveButtons = true;
            setTimeout(() => {
              this.$router.push({
                name: 'event-records',
                params: { eventId: this.event.id },
              });
            }, 2000);
          }
        });
    },

    getSanitizedFormInputsForSubmission() {
      const questions = [...this.formQuestions];

      questions.forEach((question) => {
        // Custom logic for checkboxes
        // Backend expects question_uuid checkboxOptGroup for checkbox custom questions with options
        if (question.is_custom_question && question.question_uuid === 'checkbox' && question.options !== null) {
          question.question_uuid = 'checkboxOptGroup';// eslint-disable-line
        }
        delete question.index; // eslint-disable-line
        delete question.vuexId; // eslint-disable-line
      });

      return questions;
    },

    handleReplaceNewQuestionSubmit() {
      const indexOfQuestionToBeReplaced = this.formQuestions.findIndex((question) => question.vuexId === this.questionToReplace.from.vuexId);
      const questions = [...this.formQuestions];
      questions[indexOfQuestionToBeReplaced] = {
        ...this.questionToReplace.to,
        layout: this.questionToReplace.from.layout,
        vuexId: this.questionToReplace.from.vuexId,
      };

      // Add layout and screen configs for dynamic form questions

      // If the question has an id then it means it has been saved in the database, otherwise replacing a question has no impact.
      if (this.questionToReplace.from.id !== undefined) {
        const {
          label,
          question_uuid, //eslint-disable-line
          html_form_input_id, //eslint-disable-line
        } = this.questionToReplace.from;

        questions[indexOfQuestionToBeReplaced].replaced_question_details = {
          label,
          question_uuid, //eslint-disable-line
          html_form_input_id, //eslint-disable-line
        };
      }

      this.setFormQuestions(questions);

      this.ui.showReplaceModal = false;
      this.questionToReplace = {
        from: null,
        to: null,
      };
    },

    indexQuestions() {
      const indexedQuestions = this.formQuestions.map((q, index) => ({
        ...q,
        index,
      }));
      this.setFormQuestions(indexedQuestions);
    },

    toggleThemeBuilder() {
      this.ui.showThemeBuilder = !this.ui.showThemeBuilder;
    },

  },
};
</script>

<style lang="less" scoped>
.PageHeader-container {
  flex-basis: 90%;
}

.DynamicForm-wrapper {
  position: relative;
  max-width: 700px;
  margin: 0 auto;
}

.QuestionOptions-container {
  position: absolute;
  display: inline-block;
  background: white;
  border: 1px solid #CCCCCC;
  border-radius: 4px;
  padding: 0 3px;
  top: 22px;
  left: 57px;
  z-index: 10;
  height: 23px;

  span.Icon-s {
    font-size: 12px;
    margin: 0 4px;
  }
}

.ScreenActions {
  position: relative;
  margin: 0 auto;
  max-width: 700px;
  height: 80px;

  .ScreenNavigationArrow {
    min-width: 45px;
    width: 45px;
    padding: 10px 0;
  }

  &.disabled {
    button {
      cursor: not-allowed;
      pointer-events: none;
      color: #ccc;

      .Icon {
        color: #ccc;

        &:hover {
          cursor: not-allowed;
        }
      }
    }
  }

}

.QuestionBuilder {
  padding: 0;
}

.FormScreensDraggable-container {
  width: 100%;
  height: 70px;
  margin-top: -10px;
}

.AddScreenHint {
  position: absolute;
  cursor: pointer;
  width: 100%;
  margin: 6px 0;
  padding: 10px;
  text-align: center;
  border-radius: 6px;
  color: #046ff0;
  border: 1px dotted #046ff0;

  &:hover {
    border-style: solid;
  }
}

.Screen-container {
  &.hidden {
    display: none;
  }
}

.SectionContainer-left {
  position: sticky;
  top: 70px;
  background: #F7F8F9 !important;
  max-height: 687px;
  overflow-y: hidden;
}

.SectionContainer-right {
  position: relative;
}

.sortable-ghost {
  box-shadow: none;
  border: 2px dotted #046FF0;
  background: transparent;
}

</style>
